// Shared project data — single source of truth
//
// Two shapes:
//   PROJECTS[]       — index metadata (card + work list)
//   CASES[slug]      — full case study content (9 canonical sections)

export const PROJECTS = [
  {
    id: 'coptic',
    num: '01',
    title: 'Coptic Deacon Training',
    role: 'Designer & Developer',
    tag: 'Native App · Personal',
    summary: 'A native iOS app for Coptic Orthodox deacons. Daily hymns, lectionary, and rank-by-rank training. Self-initiated.',
    outcome: 'Self-initiated · 2026',
    year: '2026',
    gated: false,
    tile: { bg: '#2A0E1A', image: '/case-studies/coptic/hero.svg', fit: 'cover', dark: true },
  },
  {
    id: 'iris',
    num: '02',
    title: 'Iris',
    role: 'Consultant',
    tag: 'AR · Consumer',
    summary: 'Product experience design, marketing strategy, and brand for a quiet AR contact lens. Marketing site, in-lens HUD, and iOS companion.',
    outcome: 'Self-initiated · 2026',
    year: '2026',
    gated: false,
    tile: { bg: '#F4ECDD', image: '/case-studies/iris/card.svg', fit: 'cover' },
  },
  {
    id: 'dsg',
    num: '03',
    title: "Dick's Sporting Goods",
    role: 'Group Product Design Manager',
    tag: 'Retail',
    summary: 'End-to-end retail experience across mobile commerce, loyalty, and store tech.',
    outcome: '+14% mobile checkout',
    year: '2022 to Present',
    gated: true,
    tile: { bg: '#0a3b2e', image: '/case-studies/dsg/hero.svg', fit: 'cover' },
  },
  {
    id: 'mattress',
    num: '04',
    title: 'Mattress Firm',
    role: 'Senior Director, UX',
    tag: 'E-commerce',
    summary: 'End-to-end redesign of the largest US mattress retailer.',
    outcome: '2.1× average order value',
    year: '2020 to 2022',
    gated: true,
    tile: { bg: '#1a1a1a', image: '/case-studies/mattress/hero.svg', fit: 'cover', dark: true },
  },
  {
    id: 'sleep',
    num: '05',
    title: 'Sleep.com',
    role: 'Senior Director, UX',
    tag: 'Consumer App',
    summary: 'Zero-to-one mobile app helping people live better by sleeping better.',
    outcome: '180K downloads, Q1',
    year: '2021',
    gated: false,
    tile: { bg: '#0b1740', image: '/case-studies/sleep/hero.svg', fit: 'cover' },
  },
  {
    id: 'homedepot',
    num: '06',
    title: 'Home Depot',
    role: 'Creative Director',
    tag: 'Omnichannel',
    summary: 'Bridging online & offline for complex, made-to-order products.',
    outcome: 'Shipped to 2,300 stores',
    year: '2019 to 2021',
    gated: true,
    tile: { bg: '#0f0f0f', image: '/case-studies/homedepot/hero.svg', fit: 'cover' },
  },
  {
    id: 'cyber',
    num: '07',
    title: 'Cyber Risk Insurance',
    role: 'Consultant',
    tag: 'Fintech',
    summary: 'A new product offering for a large UK insurance broker.',
    outcome: 'Launched in 11 markets',
    year: '2019',
    gated: true,
    tile: { bg: '#3a0a3a', image: '/case-studies/cyber/hero.svg', fit: 'cover' },
  },
  {
    id: 'uikit',
    num: '08',
    title: 'Mobile Design System',
    role: 'Consultant',
    tag: 'Design System',
    summary: 'A 300-component mobile UI kit built to accelerate consulting engagements.',
    outcome: 'Used on 6+ engagements',
    year: '2023',
    gated: false,
    tile: { bg: '#1b1b1e', image: '/case-studies/uikit/hero.svg', fit: 'cover' },
  },
  {
    id: 'workshops',
    num: '09',
    title: 'Design Thinking Workshops',
    role: 'Consultant',
    tag: 'Facilitation',
    summary: 'How I facilitate design thinking workshops as a design consultant.',
    outcome: 'Templates used across 10+ engagements',
    year: '2022',
    gated: false,
    tile: { bg: '#111', image: '/case-studies/workshops/hero.svg', fit: 'cover' },
  },
  {
    id: 'pharma',
    num: '10',
    title: 'Confidential · Pharma',
    role: 'Consultant',
    tag: 'Healthcare',
    summary: 'Mobile app design for a Fortune 100 pharmaceutical client.',
    outcome: 'Under NDA',
    year: '2024',
    gated: true,
    tile: { bg: '#eef2eb', image: '/case-studies/pharma/hero.svg', fit: 'cover', dark: true },
  },
];

export const CASES = {
  iris: {
    slug: 'iris',
    title: 'Iris · Integrated Reality Contact Lenses',
    subtitle: 'A self-initiated consumer AR product and the design system behind it. A daily-wear soft contact lens with a retinal micro-projector, an outward camera, and an on-device AI that lays contextual help over your field of view: translations, directions, what\'s-that-plant, where-did-I-put-my-keys.',
    tile: { bg: '#1A1613', iframe: '/iris/ui_kits/hud/index.html?scene=menu&chrome=off' },
    overview: {
      role: 'Consultant',
      scope: 'Marketing site · AR HUD · iOS companion · Identity',
    },
    subProjects: [
      {
        title: 'Product thesis',
        description: 'Grounded in 18 interviews with daily contact wearers. The dominant ask wasn\'t more features; it was less friction. "Stop making me pull out my phone." "I don\'t want to look like a dork." Iris was shaped by those two constraints: fade into the background, and only speak when it has something useful to say. One color per eye, thin linework, tight typography: the constraint is the signature.',
        thumb: '/case-studies/iris/thesis.svg',
      },
      {
        title: 'Iris Product Marketing',
        description: 'The place people learn about Iris and buy it. An editorial serif sets the tone for a category that usually reads as bro-tech. Warm paper palette, amber accent used sparingly, a quiet product hero rendered at 6× actual size because the real thing sits in your eye.',
        thumb: '/case-studies/iris/marketing.png',
        cta: { label: 'Try the prototype', href: '/iris/ui_kits/marketing/index.html' },
      },
      {
        title: 'AR HUD',
        description: 'The flagship surface, what you see through the lens. Monochromatic warm white over real-world video. A persistent five-mode switcher (Identify, Translate, Navigate, Remember, Quiet) anchors the system; everything else appears only when it has something to say. The "Quiet" mode is the honest one: most of the time you want the lens to be invisible.',
        thumb: '/case-studies/iris/hud.png',
        cta: { label: 'Try the HUD', href: '/iris/ui_kits/hud/index.html' },
      },
      {
        title: 'iOS companion app',
        description: 'Pairing, preferences, wellness metrics (wear time, blink rate, eye strain), and billing. The Today screen reports the current session with dignity: lens battery, quiet hours, and what you saved today, without the gamified streak nonsense that would undermine the calm posture of the product.',
        thumb: '/case-studies/iris/ios.png',
        thumbFit: 'contain',
        thumbBg: '#F4EFE6',
        cta: { label: 'Try the iOS app', href: '/iris/ui_kits/ios_app/index.html' },
      },
    ],
    next: { slug: 'dsg', title: "Dick's Sporting Goods" },
  },
  dsg: {
    slug: 'dsg',
    title: 'Athlete Experience Design at Dick\'s Sporting Goods',
    subtitle: 'A curated collection of product design, service design, and experience strategy work shaping end-to-end athlete journeys across Dick\'s Sporting Goods\' digital ecosystem.',
    tile: { bg: '#0a3b2e', image: '/case-studies/dsg/hero.svg' },
    overview: {
      role: 'Group Product Design Manager',
      scope: 'Product design · Service design · Experience strategy',
    },
    subProjects: [
      {
        title: 'World Cup 2026 Product Design Strategy',
        description: 'A concept and vision pitch focused on how Dick\'s Sporting Goods could position itself as Soccer\'s Headquarters leading into the 2026 World Cup. It outlines audience personas, cultural insights, experience concepts, and cross-functional opportunities across digital, retail, and brand touchpoints. I led the design vision, storyboarding, service design, and concept direction in partnership with product, creative, and executive stakeholders.',
        thumb: '/case-studies/dsg/world-cup.png',
        cta: { label: 'Download Design Strategy', href: 'https://www.dropbox.com/scl/fi/jngucd1z44u2iy2ptd362/World-Cup-2026.pdf?rlkey=vmivkrwdzqm4nsvg4f3kqy4mp&st=4fgpnxeq&dl=0' },
      },
      {
        title: 'Header & Nav Redesign',
        description: 'The global header drives 61% of all navigation events on dickssportinggoods.com. This rebuild centers athlete intent, surfaces identity, and anchors a new AI-powered search, without losing what makes the current nav feel dependable. Informed by usability (n=12), analytics audit, and A/B readiness across a six-week research-to-spec cycle.',
        thumb: '/case-studies/dsg/header-thumb.svg',
        cta: { label: 'Download PDF', href: '/dsg/pdfs/header-redesign.pdf' },
      },
      {
        title: 'Search Experience Redesign',
        description: 'Search today only recognizes what shoppers already know to type. This redesign treats the search bar as a conversation: a suggest tray that works like a store clerk, fuzzy queries that resolve to sharp results, and a zero-result rate tuned down through query-log analysis (90d), tree testing, and eight interviews.',
        thumb: '/case-studies/dsg/search-thumb.svg',
        cta: { label: 'Download PDF', href: '/dsg/pdfs/search-redesign.pdf' },
      },
      {
        title: 'Ask DICK\'S · AI Assistant',
        description: 'Most retail chatbots are suggestion boxes. Ask DICK\'S is rebuilt as a persistent, context-aware assistant that sits alongside the page, references the exact product you\'re looking at, and renders rich inline UI, size scales, kit builders, store inventory, instead of walls of text. Evaluated with prototype usability (n=10) and an intent classification audit.',
        thumb: '/case-studies/dsg/ai-thumb.svg',
        cta: { label: 'Download PDF', href: '/dsg/pdfs/ask-dsg.pdf' },
      },
      {
        title: 'Design System Foundations',
        description: 'A quarterly update on the Homefield Design System, created to communicate progress, status, and impact across Dick\'s Sporting Goods. I led the design vision, strategy, and execution, partnering closely with product, engineering, and brand teams to deliver foundational system improvements at scale.',
        thumb: '/case-studies/dsg/design-system.png',
        cta: { label: 'View Design System Update', href: 'https://www.dropbox.com/scl/fi/1gg1gpjr8aikl6uqm6jza/HomeField-Design-System-Q4-Recap.pdf?rlkey=kx5k173c3ss1hpicg8rnotjbp&st=srfvktm4&dl=0' },
      },
      {
        title: 'Purchase Journey North Star',
        description: 'A North Star vision for the athlete purchase journey at Dick\'s Sporting Goods, grounded in real behaviors, business constraints, and cross-functional collaboration. It brings together multiple bodies of work into a cohesive end-to-end experience, illustrating how athletes move from consideration through purchase, use, and upgrade. The vision is intentionally aspirational, designed to align teams around shared principles, themes, and opportunities rather than serve as a delivery roadmap.',
        thumb: '/case-studies/dsg/journey.png',
        cta: { label: 'Download Journey Vision', href: 'https://www.dropbox.com/scl/fi/ur1mz8kpsimx9swpedtoc/North-Star-Visioning-2024.pdf?rlkey=y2zaj0n0183l18tfsw1y9id9t&st=o2uh2mu0&dl=0' },
      },
      {
        title: 'Focusing on the Product Page Experience',
        description: 'A future vision for the Dick\'s Sporting Goods shopping experience, reframing the product page as the primary moment of inspiration, confidence, and decision-making for athletes. It brings together data, competitive insights, and detailed athlete scenarios to explore how richer content, personalization, and adaptive layouts could evolve product pages across web and app, along with a phased path forward.',
        thumb: '/case-studies/dsg/product-page.png',
        cta: { label: 'Download Product Vision', href: 'https://www.dropbox.com/scl/fi/n6xx66p7bjmz2m4u9stup/Dick-s-PDP-2.0.pdf?rlkey=11qrlvq2xhy5v9lvxtmhz3kd9&st=od6gb5mw&dl=0' },
      },
      {
        title: 'Service Blueprint for Online to Offline Shopping',
        description: 'A map of the end-to-end Buy Online, Pick Up In Store experience, from product discovery through post-pickup and returns. It captures customer actions, frontstage and backstage processes, ownership, SLAs, key metrics, and known failure points across digital and store operations. The blueprint is designed to support operational decision making, highlight variability across store formats, and identify opportunities to improve speed, reliability, and trust in the experience.',
        thumb: '/case-studies/dsg/bopis.png',
        cta: { label: 'Download BOPIS Blueprint', href: 'https://www.dropbox.com/scl/fi/tdrqivd7i2q7x1w5el3vs/BOPIS-Service-Blueprint-2-2025.pdf?rlkey=qi6ekydf0vka3hy20p7uoa86z&st=uf1ug14g&dl=0' },
      },
      {
        title: 'Fulfillment Filter Usability Study',
        description: 'A usability study focused on improving how athletes discover and apply shipping and pickup options while browsing products. It examines discoverability, clarity, and confidence across multiple design approaches, synthesizing qualitative feedback and quantitative signals to inform design decisions. The study concludes with clear recommendations and next steps to improve consistency, reduce friction, and better align the experience with user expectations.',
        thumb: '/case-studies/dsg/filters.png',
        cta: { label: 'Download Research Report', href: 'https://www.dropbox.com/scl/fi/05u7714oqu0eou39omkyx/Fulfillment-Filters-Usability-Test-Report.pdf?rlkey=f7vu29rm1pskn26o9is35g10f&st=7b9u5ez0&dl=0' },
      },
    ],
    next: { slug: 'mattress', title: 'Mattress Firm' },
  },
  mattress: {
    slug: 'mattress',
    title: 'Mattress Firm Interconnected Retail',
    subtitle: 'My team and I redesigned how customers interact with mattressfirm.com to shop for sleep solutions, delivering a more intuitive, educational, and engaging experience.',
    tile: { bg: '#F5EFE4', image: '/case-studies/mattress/hero.svg' },
    overview: {
      role: 'Senior Director, UX',
      scope: 'Product design · Service design · In-store technology',
    },
    subProjects: [
      {
        title: 'Covid-19 journey mapping',
        description: 'Mapped customer touchpoints across digital and in-store channels in 2020 to identify where the experience was breaking as behaviors shifted in real time. The work produced a living five-stage journey, a ranked list of broken moments, and a shared vocabulary that the digital and store teams used to set priorities together.',
        thumb: '/case-studies/mattress/journey-thumb.svg',
        cta: { label: 'View artifact', href: '/mattress/journey.html' },
      },
      {
        title: 'Mattressfirm.com redesign',
        description: 'A complete rebuild of mattressfirm.com organized around how people actually shop for sleep: by problem, by body, by budget. The new experience roughly doubled online conversion and also grew store traffic by about 30 percent, because a site that answers questions sends confident shoppers to test beds rather than be introduced to them.',
        thumb: '/case-studies/mattress/website-thumb.svg',
        cta: { label: 'View artifact', href: '/mattress/website.html' },
      },
      {
        title: 'Mattress Matcher in-store',
        description: 'Kiosks for self-start and tablets for associate-led service, running the same experience so a session started on the web could be picked up in store without losing a step. The goal was to untether the Sleep Expert from a back-of-store desk and keep the conversation happening next to the bed.',
        thumb: '/case-studies/mattress/matcher-thumb.svg',
        cta: { label: 'View artifact', href: '/mattress/matcher.html' },
      },
    ],
    next: { slug: 'sleep', title: 'Sleep.com Mobile App' },
  },
  sleep: {
    slug: 'sleep',
    title: 'Sleep.com',
    subtitle: 'A zero-to-one product incubation for Mattress Firm: a mobile app and editorial destination designed to earn America\'s trust on sleep, one night at a time.',
    tile: { bg: '#0b1740', image: '/case-studies/sleep/hero.svg', fit: 'cover' },
    overview: {
      role: 'Senior Director, UX',
      scope: 'Strategy · Product design · Brand · Editorial',
    },
    subProjects: [
      {
        title: 'Strategy framing',
        description: 'In 2021, Mattress Firm funded a product incubation around the domain sleep.com. The experience strategy presentation I led framed the opportunity for leadership: to turn a URL into America\'s trusted authority on sleep, and a retailer into its steward. Three positioning options, a recommended direction, and a staged roadmap built around an authority-first, habit-first product.',
        thumb: '/case-studies/sleep/strategy-thumb.svg',
        cta: { label: 'View artifact', href: '/sleep/strategy.html' },
      },
      {
        title: 'Customer journey mapping',
        description: 'Twelve deep interviews and twenty-four diary studies grounded a five-stage journey: struggle, notice, track, learn, rest. The map surfaced the language people actually use about their own sleep (almost never clinical), three moments of truth that make or break trust, and a shaped opportunity inventory used to prioritize what to build first.',
        thumb: '/case-studies/sleep/journey-thumb.svg',
        cta: { label: 'View artifact', href: '/sleep/journey.html' },
      },
      {
        title: 'Mobile app design',
        description: 'The Sleep.com mobile app is designed around a quiet three-touch loop: a one-tap log before bed, a one-question check-in at wake, and a weekly review that replaces streaks and scores with a short paragraph of plain English. No guilt loops, no upgrade nags. Live on the Apple App Store and Google Play.',
        thumb: '/case-studies/sleep/app-thumb.svg',
        cta: { label: 'View artifact', href: '/sleep/app.html' },
      },
      {
        title: 'Website design',
        description: 'Sleep.com on the web is the permission engine — an editorial destination organized around four pillars (Science, Rituals, Products, Experts) that earns Mattress Firm the right to recommend anything at all. Voice, typography, and information architecture were designed to read as a magazine, not a store.',
        thumb: '/case-studies/sleep/website-thumb.svg',
        cta: { label: 'View artifact', href: '/sleep/website.html' },
      },
    ],
    next: { slug: 'homedepot', title: 'Home Depot' },
  },
  homedepot: {
    slug: 'homedepot',
    title: 'Home Depot',
    subtitle: 'Professional Measure & Installation Services (2017): reimagining home improvement through digital innovation.',
    tile: { bg: '#0f0f0f', image: '/case-studies/homedepot/hero.svg' },
    overview: {
      role: 'Creative Director',
      scope: 'Product design · Service design',
    },
    subProjects: [
      {
        title: 'Opportunity & vision',
        description: 'The Home Depot set out to transform how Americans approach custom home projects. Traditionally, vital details like window, door, garage, and flooring measurements were gathered during in-home visits and recorded by hand. That analog process led to lost information and a fragmented customer experience. I framed the opportunity: a digital platform as indispensable to homeowners as Google Maps is to travelers, where every home detail could be captured, shared, and referenced with ease.',
        thumb: '/case-studies/homedepot/vision.svg',
        cta: { label: 'View artifact', href: '/homedepot/vision.html' },
      },
      {
        title: 'Customer digital experience',
        description: 'The homeowner-facing product: one place to capture measurements, photos, and notes for every custom project in the home. The design goal was to make complex data entry feel more like browsing your house than filling out a form, with clear hierarchy by room, project, and timeline so context is never lost between a quote today and an install six weeks from now.',
        thumb: '/case-studies/homedepot/customer.svg',
        cta: { label: 'View artifact', href: '/homedepot/customer.html' },
      },
      {
        title: 'Pro & installer workflow',
        description: 'The counterpart experience for measure specialists and installers. A shared record with the customer means the in-home visit becomes verification rather than data entry, and nothing gets lost in handoff between store, pro, and installer. The result is a benchmark for efficiency and clarity in home improvement, not a pile of clipboards.',
        thumb: '/case-studies/homedepot/pro.svg',
        cta: { label: 'View artifact', href: '/homedepot/pro.html' },
      },
    ],
    next: { slug: 'cyber', title: 'Cyber Risk Insurance' },
  },
  cyber: {
    slug: 'cyber',
    title: 'Beazley.com',
    subtitle: 'Introducing cyber risk insurance to customers in the United States.',
    tile: { bg: '#061a30', image: '/case-studies/cyber/hero.svg' },
    overview: {
      role: 'Consultant',
      scope: 'Product design · Web',
    },
    subProjects: [
      {
        title: 'Category framing',
        description: 'Cyber risk insurance was unfamiliar to most US small and mid-sized businesses in 2019. Beazley, a large UK broker, was entering the US with the offering. Before any interface, the work was positioning: turning a jargon-heavy, vendor-led topic into something a business owner could understand in a sentence and act on with confidence.',
        thumb: '/case-studies/cyber/framing-thumb.svg',
        cta: { label: 'View artifact', href: '/cyber/framing.html' },
      },
      {
        title: 'Product experience',
        description: 'The corporate site had to do three jobs at once: teach a new category, introduce a firm most US buyers had never heard of, and route visitors into a quote without asking them to learn a new vocabulary first. A proof-first narrative, a three-question quote, and a single next action per section.',
        thumb: '/case-studies/cyber/product-thumb.svg',
        cta: { label: 'View artifact', href: '/cyber/product.html' },
      },
      {
        title: 'Customer dashboard',
        description: 'A policyholder dashboard that makes security posture legible at a glance, ranks risk factors as critical, substantial, or moderate, and always ends in a concrete next step. The visual system balances rigor with approachability: a category that usually feels intimidating becomes a practical weekly tool.',
        thumb: '/case-studies/cyber/dashboard-thumb.svg',
        cta: { label: 'View artifact', href: '/cyber/dashboard.html' },
      },
    ],
    next: { slug: 'uikit', title: 'Mobile App Design System' },
  },
  uikit: {
    slug: 'uikit',
    title: '2023 Mobile UI Kit',
    subtitle: 'A freelance passion project started in May 2023 to fuel consulting opportunities.',
    tile: { bg: '#1b1b1e', image: '/case-studies/uikit/hero.svg' },
    overview: {
      role: 'Consultant',
      scope: 'Design system · Component library',
    },
    subProjects: [
      {
        title: 'Dark Mode',
        description: 'A full dark-mode component set covering navigation, cards, forms, dialogs, and data display. The palette is tuned for OLED screens with careful attention to hierarchy and contrast at small sizes.',
        thumb: '/case-studies/uikit/dark-thumb.svg',
        cta: { label: 'View artifact', href: '/uikit/dark.html' },
      },
      {
        title: 'Light Mode',
        description: 'The paired light theme, using the same component architecture and tokens. Swapping themes is a single variable change, which keeps the library portable across consulting engagements.',
        thumb: '/case-studies/uikit/light-thumb.svg',
        cta: { label: 'View artifact', href: '/uikit/light.html' },
      },
      {
        title: 'Wireframes',
        description: 'A grayscale wireframe layer that mirrors the component library one-to-one, designed to accelerate early-stage product thinking without getting pulled into visual polish prematurely.',
        thumb: '/case-studies/uikit/wireframes-thumb.svg',
        cta: { label: 'View artifact', href: '/uikit/wireframes.html' },
      },
      {
        title: 'Style Guidelines',
        description: 'Documentation of typography, spacing, color, iconography, and interaction patterns. The guidelines exist to make the system usable by other designers, not just me.',
        thumb: '/case-studies/uikit/styles-thumb.svg',
        cta: { label: 'View artifact', href: '/uikit/styles.html' },
      },
      {
        title: 'Fintech · Investing app',
        description: 'A dark-mode portfolio dashboard and buy-flow stood up on the kit for a consumer investing engagement. Charts, holdings, and trade confirmation were delivered on top of the same tokens and primitives as the consumer work below.',
        thumb: '/case-studies/uikit/fintech-thumb.svg',
      },
      {
        title: 'Healthtech · Patient portal',
        description: 'A calm light-mode care experience for an ambulatory clinic: today\'s appointments, medication reminders, lab trend charts, and secure messaging with the care team. Same component set, warmer sage accent.',
        thumb: '/case-studies/uikit/healthtech-thumb.svg',
      },
      {
        title: 'Retail · Loyalty and rewards',
        description: 'A member-tier dashboard with progress to next tier, redeemable rewards, and an in-store barcode pass. The kit\'s token layer let brand colors swap cleanly while the interaction model stayed constant.',
        thumb: '/case-studies/uikit/retail-thumb.svg',
      },
      {
        title: 'Travel · Itinerary planner',
        description: 'A day-view itinerary with inline flight, hotel, and reservation cards, a map slice, and a ticket pass detail. Editorial typography on cream, rendered entirely from kit primitives.',
        thumb: '/case-studies/uikit/travel-thumb.svg',
      },
      {
        title: 'Media · Streaming and podcast',
        description: 'A content-forward now-playing screen, queue, and browse grid for a media app pilot. Saturated, media-feel palette proving the kit can carry personality without breaking the system.',
        thumb: '/case-studies/uikit/media-thumb.svg',
      },
    ],
    next: { slug: 'workshops', title: 'Design Thinking Workshops' },
  },
  workshops: {
    slug: 'workshops',
    title: 'Design Thinking Workshops',
    subtitle: 'My tools and process for leading requirements gathering sessions as a UX design consultant.',
    tile: { bg: '#2a1e55', image: '/case-studies/workshops/hero.svg' },
    overview: {
      role: 'Consultant',
      scope: 'Facilitation · Research · Templates',
    },
    subProjects: [
      {
        title: 'Facilitation playbook',
        description: 'How I run a one-day design thinking workshop as a consulting deliverable. Agenda, roles, timing, and the pitfalls I now avoid. The shape is built to end with a real brief, not a photo of sticky notes.',
        thumb: '/case-studies/workshops/playbook-thumb.svg',
        cta: { label: 'View artifact', href: '/workshops/playbook.html' },
      },
      {
        title: 'Persona templates',
        description: 'The template I bring into every requirements-gathering workshop. Four anchors (motivations, pain points, context, behaviors) and a sample filled persona that walks through how each field becomes a downstream design decision. The blank template is linked inside as a Google Drive download.',
        thumb: '/case-studies/workshops/personas-thumb.svg',
        cta: { label: 'View artifact', href: '/workshops/personas.html' },
      },
      {
        title: 'Facilitator toolkit',
        description: 'The bench of exercises I reach for inside a workshop: warm-ups, affinity mapping, dot voting, How Might We reframes, crazy 8s, assumption mapping. Short entries framed by when I use each one, why, and the failure mode it exists to prevent.',
        thumb: '/case-studies/workshops/facilitation-thumb.svg',
        cta: { label: 'View artifact', href: '/workshops/facilitation.html' },
      },
    ],
    next: { slug: 'coptic', title: 'Coptic Deacon Training' },
  },
  coptic: {
    slug: 'coptic',
    title: 'Coptic Deacon Training',
    subtitle: 'A daily training app for the five ranks of the Coptic Orthodox diaconate, one of the oldest continuous Christian traditions in the world. Self-initiated. Designed and prototyped end-to-end.',
    tile: { bg: '#2A0E1A', image: '/case-studies/coptic/hero.svg' },
    overview: {
      role: 'Designer & Developer',
      scope: 'Product · Visual design · Front-end prototype',
    },
    subProjects: [
      {
        title: 'About the church · The deacon\'s inheritance',
        description: 'The Coptic Orthodox Church was founded by Saint Mark the Evangelist in Alexandria around AD 42, less than a decade after the Ascension. It is among the oldest Christian institutions in the world, and the Catechetical School of Alexandria, traditionally founded by Mark himself, is considered the oldest Christian school of theology. The current Pope of Alexandria, Tawadros II, is the 118th in unbroken apostolic succession from St. Mark. Today the church serves roughly ten million people worldwide. Every deacon trained by this app inherits a liturgy, a chant tradition, and a service older than most nations: this case study assumes the audience is unfamiliar with that context, so the features below are explained in terms of the diaconal duties they support.',
        thumb: '/case-studies/coptic/foundation-thumb.svg',
      },
      {
        title: 'Daily preparation · Today',
        description: 'A deacon shows up to liturgy with the day\'s hymns, responses, and readings already in his bones. The Today screen frames a single-tap commitment: today\'s tone, today\'s hymn, today\'s Gospel, and a quiet streak count that rewards habit, not vanity. Service requires preparation. The app turns preparation into a five-minute morning ritual.',
        thumb: '/case-studies/coptic/screens/today.png',
      },
      {
        title: 'Five ranks, one ladder · Lessons',
        description: 'The diaconate is not a flat title. A boy is ordained Epsaltos (Chanter), then Anagnostis (Reader), then Ypodiakon (Subdeacon), then Diakon, then Archdeacon. Each rank carries new responsibilities at the altar. The Lessons tab mirrors that ladder: a curriculum where each rank unlocks specific skills and accountabilities, and progress visibly compounds.',
        thumb: '/case-studies/coptic/screens/lessons.png',
      },
      {
        title: 'The chanter\'s primary job · Hymns library and tri-script player',
        description: 'Epsaltos and Anagnostis lead the congregation through dozens of hymns spanning five tones (Annual, Festive, Lenten, Sad and Pascha, Palm Sunday) and seasonal occasions. The hymn player shows three synchronized layers: the original Coptic script, a transliteration for those still learning the script, and a line-for-line English translation. Each layer can be toggled. Lyrics scroll line-by-line as the hymn plays, so a young chanter can sing along until the words stick.',
        thumb: '/case-studies/coptic/screens/hymns.png',
      },
      {
        title: 'Rehearsal you can hear · Practice',
        description: 'Deacons sing in front of a congregation. Sight-reading a hymn for the first time at the altar is unforgiving. Practice is a private rehearsal space: record yourself singing a hymn, play it back against the master recording, and self-correct without shame. Stacked waveforms and side-by-side timing make pronunciation gaps visible.',
        thumb: '/case-studies/coptic/screens/practice.png',
      },
      {
        title: 'Recall under pressure · Quiz',
        description: 'In the middle of a litany a deacon cannot reach for a phone. He has to remember. Quiz mode tests him on response prompts, hymn-tone identification, and Coptic-to-English vocabulary across all five ranks. Sessions are short, spaced over weeks, and weighted toward the items most likely to come up in this week\'s actual services.',
        thumb: '/case-studies/coptic/screens/quiz.png',
      },
      {
        title: 'Knowing the day · Calendar and Lectionary',
        description: 'Every day in the Coptic calendar carries its own readings: Synaxarium, Pauline, Catholic, Praxis, Psalm, and Gospel, plus a tone for that season. A new deacon often does not know which is which. Calendar and Lectionary surface today\'s liturgical context up front, so a Reader can prepare the right Epistle in the right tone without a spreadsheet of feast days.',
        thumb: '/case-studies/coptic/screens/lectionary.png',
      },
    ],
    next: { slug: 'iris', title: 'Iris' },
  },
};

export const BIO_FACTS = [
  { k: 'Role', v: 'Fractional Chief Product Designer' },
  { k: 'Experience', v: '20 years · 30+ engagements' },
  { k: 'Based', v: 'The Woodlands, TX · US Central' },
  { k: 'Availability', v: 'Open to senior design leadership roles' },
  { k: 'Type', v: 'Fractional · Advisory · Zero-to-one' },
];

export const ACCENTS = {
  clay:   'oklch(0.58 0.15 42)',
  forest: 'oklch(0.52 0.12 150)',
  cobalt: 'oklch(0.48 0.18 260)',
  amber:  'oklch(0.72 0.16 75)',
  rose:   'oklch(0.62 0.15 10)',
};
